import {
  X,
  Users,
  Package,
  Receipt,
  Zap,
  MessageSquare,
  Lightbulb,
  Clock,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Constants } from "@/constants";

interface UnlockFeaturePopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  features?: Array<{
    title: string;
    description: string;
  }>;
  ctaTitle?: string;
  ctaDescription?: string;
  buttonText?: string;
  benefits?: string[];
}

export function UnlockFeaturePopup({
  isOpen,
  onClose,
  title = "Build Customer Relationships & Drive Repeat Sales",
  description = "Easily manage customers and run powerful automated campaigns.",
  buttonText = "Install CRM"
}: UnlockFeaturePopupProps) {
  if (!isOpen) return null;

  const handleSubscribe = () => {
    window.open(Constants.CLOVER_MARKETPLACE_URL, "_blank");
    onClose();
  };

  const handleMaybeLater = () => {
    onClose();
  };

  // Default customer management features
  const customerManagementFeatures = [
    {
      title: "Customers",
      description: "Manage your customer base easily",
      icon: Users,
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      iconColor: "text-green-600"
    },
    {
      title: "Products",
      description: "Connect products to customers",
      icon: Package,
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      iconColor: "text-purple-600"
    },
    {
      title: "Transactions",
      description: "See purchase and engagement history",
      icon: Receipt,
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      iconColor: "text-yellow-600"
    }
  ];

  // Default campaign features
  const campaignFeatures = [
    {
      title: "Automated Campaigns",
      description: "Run smart campaigns automatically",
      icon: Zap,
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      iconColor: "text-green-600"
    },
    {
      title: "SMS Campaigns",
      description: "Send targeted messages instantly",
      icon: Send,
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      iconColor: "text-red-600"
    },
    {
      title: "Campaign Ideas",
      description: "Discover ready-to-use campaign ideas",
      icon: Lightbulb,
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      iconColor: "text-blue-600"
    },
    {
      title: "Campaign History",
      description: "Track what worked before",
      icon: Clock,
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
      iconColor: "text-gray-600"
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl relative overflow-hidden">
        {/* Header with Teal Background */}
        <div className="bg-teal-600 text-white px-6 pt-6 pb-4 relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-teal-600 z-10"
          >
            <X className="h-5 w-5 text-white" />
            <span className="sr-only">Close</span>
          </button>
          <div className="flex items-start gap-3 pr-8">
            <Users className="h-8 w-8 flex-shrink-0 text-white" />
            <div>
              <h2 className="text-xl font-bold text-white mb-1">{title}</h2>
              <p className="text-white/90 text-sm">{description}</p>
            </div>
          </div>
        </div>

        {/* Main Content with Two Columns */}
        <div className="p-6 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer Management Column */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-gray-700" />
                <h3 className="text-base font-semibold text-gray-900">
                  Customer Management
                </h3>
              </div>

              {customerManagementFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className={`${feature.bgColor} ${feature.borderColor} border rounded-lg p-3`}
                  >
                    <div className="flex items-start gap-2.5">
                      <IconComponent
                        className={`h-5 w-5 ${feature.iconColor} flex-shrink-0 mt-0.5`}
                      />
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                          {feature.title}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Campaigns Column */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-4 w-4 text-gray-700" />
                <h3 className="text-base font-semibold text-gray-900">
                  Campaigns
                </h3>
              </div>

              {campaignFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className={`${feature.bgColor} ${feature.borderColor} border rounded-lg p-3`}
                  >
                    <div className="flex items-start gap-2.5">
                      <IconComponent
                        className={`h-5 w-5 ${feature.iconColor} flex-shrink-0 mt-0.5`}
                      />
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                          {feature.title}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {feature.description}
                        </p>
                        {feature.title === "SMS Campaigns" && (
                          <span className="inline-block h-1.5 w-1.5 bg-black rounded-full ml-1 mt-1" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer with Teal Background */}
        <div className="bg-teal-600 text-white px-6 py-4 flex items-center justify-between rounded-b-lg">
          <Button
            onClick={handleSubscribe}
            className="bg-white hover:bg-gray-100 text-teal-600 flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold"
          >
            <Zap className="h-4 w-4" />
            {buttonText}
          </Button>
          <button
            onClick={handleMaybeLater}
            className="text-white hover:text-white/80 text-sm font-medium transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}
